import {useRef, useState} from 'react';

import {useUpdateEffect} from 'common';

import {SelectState} from '../types';

import type {LastResult, SelectContextProps, SelectOption, SelectProps, SelectSingleValue} from '../types';

type TUseOptionsState = (
  initial?: SelectOption[]
) => [SelectOption[], (opts: SelectOption[]) => void, (opts: SelectOption[]) => void];

const useOptionsState: TUseOptionsState = (initial = []) => {
  const [options, setOptions] = useState<SelectOption[]>(initial);

  const upsertOptions = (newOptions: SelectOption[]) => {
    const curOptionsValue = options.reduce(
      (acc, option) => {
        acc[option.value] = true;
        return acc;
      },
      {} as Record<SelectSingleValue, true>
    );

    const diffOptions = newOptions.filter(option => !curOptionsValue[option.value]);
    if (!diffOptions.length) {
      return;
    }

    setOptions([...options, ...diffOptions]);
  };

  return [options, setOptions, upsertOptions];
};

export const useOptions = (
  baseOptions: SelectProps['options'],
  filter: SelectProps['filter'],
  onLoadMore: SelectProps['onLoadMore']
) => {
  const [state, setStateValue] = useState<SelectState>(SelectState.Default);
  const [options, setOptions, upsertOptions] = useOptionsState(baseOptions);
  const [filtred, setFiltred, upsertFiltred] = useOptionsState();

  const lastQuery = useRef<string>('');
  const lastFilterResult = useRef<LastResult>();
  const lastMoreResult = useRef<LastResult>();

  const timeout = useRef<ReturnType<typeof setTimeout>>();

  const setState: SelectContextProps['setState'] = newState => {
    if (state === SelectState.Filtred && newState === SelectState.Default) {
      lastFilterResult.current = undefined;
      lastQuery.current = '';

      setFiltred([]);
    }

    setStateValue(newState);
  };

  const filterOptions = (query: string) => {
    if (!filter) {
      return;
    }

    setState(SelectState.Searching);

    filter
      .cb(query, options, lastFilterResult.current)
      .then(newResult => {
        upsertOptions(newResult.options);

        setState(SelectState.Filtred);
        setFiltred(newResult.options);

        lastQuery.current = query;
        lastFilterResult.current = newResult;
      })
      .catch(e => {
        setState(SelectState.Default);

        // eslint-disable-next-line  no-console
        console.error(e);
      });
  };

  const loadMore: SelectContextProps['loadMore'] = target => {
    if (
      !target ||
      !onLoadMore ||
      state === SelectState.LoadingMore ||
      !target.scrollTop ||
      target.scrollTop + target.clientHeight < target.scrollHeight - 200
    ) {
      return;
    }

    clearTimeout(timeout.current);

    timeout.current = setTimeout(() => {
      if (state === SelectState.Filtred) {
        if (filter && lastQuery.current) {
          filter
            .cb(lastQuery.current, options, lastFilterResult.current)
            .then(newResult => {
              upsertOptions(newResult.options);

              setState(SelectState.Filtred);
              upsertFiltred(newResult.options);

              lastFilterResult.current = newResult;
            })
            .catch(e => {
              setState(SelectState.Default);

              // eslint-disable-next-line  no-console
              console.error(e);
            });
        }

        return;
      }

      setState(SelectState.LoadingMore);

      onLoadMore(options, lastMoreResult.current)
        .then(newResult => {
          upsertOptions(newResult.options);

          lastMoreResult.current = newResult;
        })
        // eslint-disable-next-line  no-console
        .catch(console.error)
        .finally(() => {
          setState(SelectState.Default);
        });
    }, 100);
  };

  useUpdateEffect(() => {
    setOptions(baseOptions);

    lastMoreResult.current = undefined;
  }, [baseOptions]);

  return {
    options,
    filtred,
    state,
    setState,
    filterOptions,
    loadMore,
  };
};
